import React, { useState, useEffect } from 'react';
import { networkManager } from '../../utils/errorHandling';

/**
 * Connection Status Indicator
 * Shows online/offline status and queued operations count
 */
function ConnectionStatus() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [queuedOperations, setQueuedOperations] = useState(0);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Listen for network status changes
    const handleNetworkChange = (status) => {
      setIsOnline(status.isOnline);
      setQueuedOperations(status.operationQueue?.length || 0);
    };

    networkManager.addListener(handleNetworkChange);

    // Initial state
    setQueuedOperations(networkManager.operationQueue.length);

    return () => {
      networkManager.removeListener(handleNetworkChange);
    };
  }, []);

  // Don't show if online with no queued operations
  if (isOnline && queuedOperations === 0) {
    return null;
  }

  return (
    <div className="fixed top-4 right-4 z-50">
      <div 
        className={`
          flex items-center px-3 py-2 rounded-lg shadow-lg cursor-pointer transition-all duration-300
          ${isOnline 
            ? 'bg-yellow-50 border border-yellow-200 text-yellow-800' 
            : 'bg-red-50 border border-red-200 text-red-800'
          }
        `}
        onClick={() => setShowDetails(!showDetails)}
      >
        {/* Status Indicator */}
        <div 
          className={`
            w-2 h-2 rounded-full mr-2
            ${isOnline ? 'bg-yellow-500' : 'bg-red-500 animate-pulse'}
          `}
        />

        {/* Status Text */}
        <div className="flex items-center space-x-2">
          <span className="text-sm font-medium">
            {isOnline ? 'Connected' : 'Connection Lost'}
          </span>
          
          {queuedOperations > 0 && (
            <span className="text-xs bg-white bg-opacity-70 px-2 py-1 rounded-full">
              {queuedOperations} queued
            </span>
          )}

          {/* Expand/Collapse Icon */}
          <svg 
            className={`w-4 h-4 transform transition-transform ${showDetails ? 'rotate-180' : ''}`}
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Detailed Status Panel */}
      {showDetails && (
        <div className="mt-2 p-4 bg-white rounded-lg shadow-lg border border-gray-200 min-w-[280px]">
          <div className="space-y-3">
            {/* Connection Status */}
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Network Status:</span>
              <div className="flex items-center">
                <div 
                  className={`
                    w-2 h-2 rounded-full mr-2
                    ${isOnline ? 'bg-green-500' : 'bg-red-500'}
                  `}
                />
                <span className={`text-sm ${isOnline ? 'text-green-600' : 'text-red-600'}`}>
                  {isOnline ? 'Online' : 'Offline'}
                </span>
              </div>
            </div>

            {/* Queued Operations */}
            {queuedOperations > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Queued Operations:</span>
                <span className="text-sm text-orange-600">{queuedOperations}</span>
              </div>
            )}

            {/* Status Message */}
            <div className="pt-2 border-t border-gray-200">
              <p className="text-xs text-gray-600">
                {isOnline ? (
                  queuedOperations > 0 ? (
                    'Connection restored. Processing queued changes...'
                  ) : (
                    'All changes are being synchronized in real-time.'
                  )
                ) : (
                  'You are currently offline. Changes will be synchronized when connection is restored.'
                )}
              </p>
            </div>

            {/* Actions */}
            <div className="flex space-x-2 pt-2">
              {!isOnline && (
                <button
                  onClick={() => window.location.reload()}
                  className="flex-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 py-1 px-2 rounded transition-colors"
                >
                  Retry Connection
                </button>
              )}
              
              {queuedOperations > 0 && (
                <button
                  onClick={() => {
                    networkManager.clearQueue();
                    setQueuedOperations(0);
                    setShowDetails(false);
                  }}
                  className="flex-1 text-xs bg-red-100 hover:bg-red-200 text-red-700 py-1 px-2 rounded transition-colors"
                >
                  Clear Queue
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Simple connection dot for minimal UI
 */
export function ConnectionDot() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleNetworkChange = (status) => {
      setIsOnline(status.isOnline);
    };

    networkManager.addListener(handleNetworkChange);

    return () => {
      networkManager.removeListener(handleNetworkChange);
    };
  }, []);

  return (
    <div 
      className={`
        w-3 h-3 rounded-full border-2 border-white
        ${isOnline ? 'bg-green-500' : 'bg-red-500 animate-pulse'}
      `}
      title={isOnline ? 'Connected' : 'Connection Lost'}
    />
  );
}

export default ConnectionStatus;
